
import React, { useState } from 'react';
import { SPELLS } from '../data/spells';
import { SPELL_SLOTS } from '../data/constants';
import './Spells.css';
import Tooltip from './Tooltip';

const Spells = ({ character, toggleSpell }) => {
    const [filterLevel, setFilterLevel] = useState('All');

    // Determined by Class usually, but for now we look at classes array in SPELLS
    const classSpells = SPELLS.filter(spell => spell.classes.includes(character.class));

    // Calculate max prepared/known (Simplified)
    const spellAttribute = ['Wizard', 'Artificer'].includes(character.class) ? 'intelligence' :
        ['Cleric', 'Druid', 'Ranger'].includes(character.class) ? 'wisdom' : 'charisma';

    const modifier = Math.floor((character.abilityScores[spellAttribute] - 10) / 2);
    const maxPrepared = Math.max(1, character.level + modifier);

    // Spell Slots Logic
    const casterType = ['Paladin', 'Ranger'].includes(character.class) ? 'HalfCaster' : 'FullCaster';
    const slots = SPELL_SLOTS[casterType][character.level] || [];
    // slots is array like [4, 3] -> Level 1 has 4, Level 2 has 3

    // Helper to get slots for a level
    const getSlotsForLevel = (level) => {
        if (level === 0) return '∞';
        return slots[level - 1] || 0;
    };

    // Filter by Level
    const filteredSpells = classSpells.filter(spell => {
        if (filterLevel === 'All') return true;
        return spell.level === parseInt(filterLevel);
    });

    const preparedSpellsList = character.spells?.prepared || [];
    const isSpellPrepared = (spellName) => preparedSpellsList.includes(spellName);

    // Cantrips are "Known" not "Prepared" (usually), and don't count towards the limit
    const preparedCount = preparedSpellsList.filter(name => {
        const s = SPELLS.find(sp => sp.name === name);
        return s && s.level > 0;
    }).length;

    return (
        <div className="spells-page-container">
            <h2>Spell Management</h2>

            <div className="spell-stats">
                <div className="stat-box">
                    <label>Spell Attribute</label>
                    <div className="value">{spellAttribute.charAt(0).toUpperCase() + spellAttribute.slice(1)}</div>
                </div>
                <div className="stat-box">
                    <label>
                        Spell Save DC
                        <Tooltip text={`8 + PB (${character.proficiencyBonus}) + Mod (${modifier})`}>
                            <span className="help-icon">?</span>
                        </Tooltip>
                    </label>
                    <div className="value">{8 + character.proficiencyBonus + modifier}</div>
                </div>
                <div className="stat-box">
                    <label>
                        Attack Bonus
                        <Tooltip text={`PB (${character.proficiencyBonus}) + Mod (${modifier})`}>
                            <span className="help-icon">?</span>
                        </Tooltip>
                    </label>
                    <div className="value">+{character.proficiencyBonus + modifier}</div>
                </div>
                <div className="stat-box">
                    <label>
                        Max Prepared
                        <Tooltip text={`Level (${character.level}) + Mod (${modifier}) (Excludes Cantrips)`}>
                            <span className="help-icon">?</span>
                        </Tooltip>
                    </label>
                    <div className="value">{maxPrepared}</div>
                </div>
            </div>

            <div className="spells-filters">
                <label>Filter Level: </label>
                <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
                    <option value="All">All</option>
                    <option value="0">Cantrips (Level 0)</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(lvl => (
                        <option key={lvl} value={lvl}>Level {lvl} ({getSlotsForLevel(lvl)} Casts/Day)</option>
                    ))}
                </select>
            </div>

            <div className="cantrips-section">
                <h3>Cantrips (At Will)</h3>
                <div className="spells-list">
                    {/* Granted Cantrips (from race/class features) */}
                    {(character.grantedSpells || [])
                        .map(name => SPELLS.find(s => s.name === name))
                        .filter(s => s && s.level === 0)
                        .map(spell => (
                            <div key={spell.name} className="spell-card cantrip-card granted">
                                <div className="spell-header">
                                    <h4>{spell.name}</h4>
                                    <span className="spell-locked-badge" title="Granted by race/class feature">🔒 Granted</span>
                                </div>
                                <div className="spell-details">
                                    <span>{spell.castingTime}</span>
                                    <span> • </span>
                                    <span>{spell.range}</span>
                                </div>

                                <div className="spell-tooltip">{spell.description}</div>
                            </div>
                        ))
                    }

                    {/* Learned Cantrips (manually added) */}
                    {preparedSpellsList
                        .filter(name => !(character.grantedSpells || []).includes(name)) // Exclude granted spells
                        .map(name => SPELLS.find(s => s.name === name))
                        .filter(s => s && s.level === 0)
                        .map(spell => (
                            <div key={spell.name} className="spell-card cantrip-card">
                                <div className="spell-header">
                                    <h4>{spell.name}</h4>
                                    <button className="spell-toggle-btn remove" onClick={() => toggleSpell(spell.name, 'prepared')}>Forget</button>
                                </div>
                                <div className="spell-details">
                                    <span>{spell.castingTime}</span>
                                    <span> • </span>
                                    <span>{spell.range}</span>
                                </div>

                                <div className="spell-tooltip">{spell.description}</div>
                            </div>
                        ))
                    }
                    {/* If no cantrips at all */}
                    {(character.grantedSpells || []).filter(name => SPELLS.find(s => s.name === name)?.level === 0).length === 0 &&
                        preparedSpellsList.filter(name => SPELLS.find(s => s.name === name)?.level === 0).length === 0 && (
                            <div className="empty-message">No cantrips known. Learn some below!</div>
                        )}
                </div>
            </div>

            <div className="spells-section">
                <h3>Prepared Spells ({preparedCount}/{maxPrepared})</h3>

                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => {
                    const spellsOfLevel = preparedSpellsList
                        .map(name => SPELLS.find(s => s.name === name))
                        .filter(s => s && s.level === level);

                    if (spellsOfLevel.length === 0) return null;

                    const slotsForLevel = getSlotsForLevel(level);

                    return (
                        <div key={level} className="prepared-level-group">
                            <h4 className="level-header">
                                Level {level}
                                <span className="slot-badge" title="You can cast this many spells of this level per day">
                                    {slotsForLevel} Casts/Day
                                </span>
                            </h4>
                            <div className="spells-list">
                                {spellsOfLevel.map(spell => (
                                    <div key={spell.name} className="spell-card prepared">
                                        <div className="spell-header">
                                            <h4>{spell.name}</h4>
                                            <button className="spell-toggle-btn remove" onClick={() => toggleSpell(spell.name, 'prepared')}>
                                                {level === 0 ? 'Forget' : 'Unprepare'}
                                            </button>
                                        </div>
                                        <div className="spell-details">
                                            <span>{spell.castingTime}</span>
                                            <span> • </span>
                                            <span>{spell.range}</span>
                                        </div>
                                        <div className="spell-tooltip">{spell.description}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="spells-section">
                <h3>Available Spells</h3>
                <div className="spells-list">
                    {filteredSpells.map(spell => {
                        const isGranted = (character.grantedSpells || []).includes(spell.name);
                        if (isSpellPrepared(spell.name) || isGranted) return null;

                        const slotsAvailable = getSlotsForLevel(spell.level);
                        const noSlots = spell.level > 0 && slotsAvailable === 0;

                        return (
                            <div key={spell.name} className={`spell-card ${noSlots ? 'unavailable' : ''}`}>
                                <div className="spell-header">
                                    <h4>{spell.name}</h4>
                                    <button
                                        className="spell-toggle-btn add"
                                        onClick={() => toggleSpell(spell.name, 'prepared')}
                                        disabled={(preparedCount >= maxPrepared && spell.level > 0) || noSlots}
                                    >
                                        {spell.level === 0 ? 'Learn' : 'Prepare'}
                                    </button>
                                </div>
                                <div className="spell-details">
                                    <span className="spell-level-tag">{spell.level === 0 ? 'Cantrip' : `Lvl ${spell.level}`}</span>
                                    <span> • </span>
                                    <span>{spell.castingTime}</span>
                                    <span> • </span>
                                    <span>{spell.range}</span>
                                </div>
                                <div className="spell-tooltip">{spell.description}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Spells;
